/** Tab 8: Nominees + Emergency Contacts */
import { useState } from 'react'
import {
  Button, Card, Form, Input, message, Modal, Select, InputNumber, Switch, Space, Table, Tag,
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  joiningApi,
  type JoiningDetail,
  type NomineeRow,
  type EmergencyContactRow,
} from '@/features/hr/joiningApi'
import { NOMINEE_RELATIONSHIPS } from '@/shared/types/hr'

const RELATIONSHIP_OPTIONS = NOMINEE_RELATIONSHIPS.map((r) => ({
  label: r.replace(/_/g, ' '), value: r,
}))

interface Props { detail: JoiningDetail }

export default function NomineeTab({ detail }: Props) {
  const qc = useQueryClient()
  const [nomineeOpen, setNomineeOpen] = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  const [nomineeForm] = Form.useForm()
  const [contactForm] = Form.useForm()

  const addNominee = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      joiningApi.addNominee(detail.id, body as Parameters<typeof joiningApi.addNominee>[1]),
    onSuccess: () => {
      message.success('Nominee added')
      setNomineeOpen(false)
      nomineeForm.resetFields()
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  const addContact = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      joiningApi.addEmergencyContact(
        detail.id,
        body as Parameters<typeof joiningApi.addEmergencyContact>[1],
      ),
    onSuccess: () => {
      message.success('Contact added')
      setContactOpen(false)
      contactForm.resetFields()
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  const nomineeCols = [
    { title: 'Name', dataIndex: 'nominee_name' },
    { title: 'Relationship', dataIndex: 'relationship' },
    { title: 'Share %', dataIndex: 'share_percent' },
    { title: 'Type', dataIndex: 'nomination_type' },
    {
      title: 'Primary',
      dataIndex: 'is_primary',
      render: (v: boolean) => (v ? <Tag color="green">Primary</Tag> : null),
    },
  ]

  const contactCols = [
    { title: 'Name', dataIndex: 'contact_name' },
    { title: 'Relationship', dataIndex: 'relationship' },
    { title: 'Phone', dataIndex: 'phone' },
    {
      title: 'Primary',
      dataIndex: 'is_primary',
      render: (v: boolean) => (v ? <Tag color="green">Primary</Tag> : null),
    },
  ]

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card
        size="small"
        title="Nominees"
        extra={
          <Button icon={<PlusOutlined />} size="small" onClick={() => setNomineeOpen(true)}>
            Add Nominee
          </Button>
        }
      >
        <Table<NomineeRow>
          rowKey="id"
          size="small"
          dataSource={detail.nominees}
          columns={nomineeCols}
          pagination={false}
        />
      </Card>

      <Card
        size="small"
        title="Emergency Contacts"
        extra={
          <Button icon={<PlusOutlined />} size="small" onClick={() => setContactOpen(true)}>
            Add Contact
          </Button>
        }
      >
        <Table<EmergencyContactRow>
          rowKey="id"
          size="small"
          dataSource={detail.emergency_contacts}
          columns={contactCols}
          pagination={false}
        />
      </Card>

      <Modal
        title="Add Nominee"
        open={nomineeOpen}
        onCancel={() => setNomineeOpen(false)}
        onOk={() => nomineeForm.submit()}
        confirmLoading={addNominee.isPending}
      >
        <Form form={nomineeForm} layout="vertical" onFinish={(v) => addNominee.mutate(v)}>
          <Form.Item name="nominee_name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="relationship" label="Relationship" rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder="Pick relationship"
              options={RELATIONSHIP_OPTIONS}
            />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item name="share_percent" label="Share %">
            <InputNumber style={{ width: '100%' }} min={0} max={100} />
          </Form.Item>
          <Form.Item name="nomination_type" label="Type" initialValue="PF">
            <Select
              options={[
                { value: 'PF', label: 'PF' },
                { value: 'ESI', label: 'ESI' },
                { value: 'GRATUITY', label: 'Gratuity' },
                { value: 'INSURANCE', label: 'Insurance' },
              ]}
            />
          </Form.Item>
          <Form.Item name="is_primary" label="Primary" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add Emergency Contact"
        open={contactOpen}
        onCancel={() => setContactOpen(false)}
        onOk={() => contactForm.submit()}
        confirmLoading={addContact.isPending}
      >
        <Form form={contactForm} layout="vertical" onFinish={(v) => addContact.mutate(v)}>
          <Form.Item name="contact_name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="relationship" label="Relationship" rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder="Pick relationship"
              options={RELATIONSHIP_OPTIONS}
            />
          </Form.Item>
          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="alt_phone" label="Alt Phone">
            <Input />
          </Form.Item>
          <Form.Item name="is_primary" label="Primary" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  )
}
